import { useLocation, useNavigate } from '@tanstack/react-router';
import { ChevronRight, ChevronDown, Settings, LogOut } from 'lucide-react';
import * as DropdownMenu from '@radix-ui/react-dropdown-menu';

import { useAuthStore } from '@/stores/authStore';
import { apiClient } from '@/lib/apiClient';
import { Avatar } from '@/features/Profile/Avatar';

const routeTitles: Record<string, string> = {
  '/admin/dashboard': 'Dashboard',
  '/admin/orgs': 'Organizations',
  '/admin/orgs/new': 'New Organization',
  '/admin/batches': 'Batches',
  '/admin/curriculum': 'Curriculum',
  '/admin/users': 'Users',
  '/admin/problems': 'Problems',
  '/admin/analytics': 'Analytics',
  '/admin/settings': 'Settings',
};

function getPageTitle(pathname: string): string {
  if (routeTitles[pathname]) return routeTitles[pathname];
  if (pathname.startsWith('/admin/orgs/') && pathname !== '/admin/orgs/new') return 'Organization';
  if (pathname.startsWith('/admin/curriculum/')) return 'Curriculum Editor';
  return 'Admin';
}

interface AdminTopBarProps {
  actions?: React.ReactNode;
}

export function AdminTopBar({ actions }: AdminTopBarProps) {
  const location = useLocation();
  const { user, clearAuth } = useAuthStore();
  const navigate = useNavigate();
  const title = getPageTitle(location.pathname);
  const username = user?.username ?? 'Admin';

  const handleLogout = async () => {
    try { await apiClient.post('/api/v1/auth/logout'); } catch { /* ignore */ }
    clearAuth();
    navigate({ to: '/' });
  };

  return (
    <div className="flex h-[90px] flex-shrink-0 items-center border-b border-[#f1f1f1] bg-white px-8">
      {/* Breadcrumb */}
      <div className="flex items-center gap-1.5 text-sm">
        <span className="text-muted-foreground">Admin</span>
        {title !== 'Admin' && (
          <>
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground/50" />
            <span className="font-medium text-primary">{title}</span>
          </>
        )}
      </div>

      {/* Right side */}
      <div className="ml-auto flex items-center gap-3">
        {actions}
        {/* User menu */}
        <DropdownMenu.Root>
          <DropdownMenu.Trigger asChild>
            <button
              type="button"
              className="flex items-center gap-2 rounded-xl px-1.5 py-1 outline-none transition-colors hover:bg-muted"
            >
              <Avatar src={user?.avatarUrl} name={username} size={28} />
              <div className="hidden sm:block">
                <div className="text-[12px] font-medium text-foreground leading-none">{username}</div>
                <div className="text-[10.5px] text-muted-foreground leading-none mt-0.5">
                  {user?.role === 'super_admin' ? 'Super Admin' : user?.role ?? ''}
                </div>
              </div>
              <ChevronDown className="hidden h-3.5 w-3.5 text-muted-foreground sm:block" />
            </button>
          </DropdownMenu.Trigger>

          <DropdownMenu.Portal>
            <DropdownMenu.Content
              className="z-50 w-[260px] rounded-xl border border-border bg-background shadow-xl"
              sideOffset={8}
              align="end"
            >
              <div className="flex items-center gap-3 p-4 pb-3">
                <Avatar src={user?.avatarUrl} name={username} size={40} />
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-foreground">{username}</p>
                  <p className="truncate text-xs text-muted-foreground">{user?.email}</p>
                </div>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              <div className="p-1.5">
                <DropdownMenu.Item
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm hover:bg-muted outline-none"
                  onSelect={() => navigate({ to: '/admin/settings' })}
                >
                  <Settings className="h-4 w-4 text-muted-foreground" />
                  <span>Settings</span>
                </DropdownMenu.Item>
              </div>

              <DropdownMenu.Separator className="h-px bg-border" />

              <div className="p-1.5">
                <DropdownMenu.Item
                  onSelect={() => handleLogout()}
                  className="flex cursor-pointer items-center gap-3 rounded-lg px-3 py-2 text-sm text-destructive hover:bg-destructive/10 outline-none"
                >
                  <LogOut className="h-4 w-4" />
                  <span>Sign out</span>
                </DropdownMenu.Item>
              </div>
            </DropdownMenu.Content>
          </DropdownMenu.Portal>
        </DropdownMenu.Root>
      </div>
    </div>
  );
}
